import {useState} from "react";
import {
    Box, Button, Checkbox, Dialog, DialogContent, FormControlLabel, IconButton, TextField, Typography
} from "@mui/material";
import {
    Close as CloseIcon,
    Lock as LockIcon
} from "@mui/icons-material";
import {RTL} from "../../cache/CacheRtl";
import {colors, borders} from "../ui/colors";

const inputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: borders.radius.md,
        backgroundColor: colors.white,
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.primary },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.primary, borderWidth: "2px" }
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.border }
};

const labelSx = { fontSize: "13px", fontWeight: 500, color: colors.textSecondary, marginBottom: "6px" };
const errorSx = { color: colors.error, fontSize: "12px", marginTop: "4px" };

const ChangePasswordDialog = ({isOpen, close, onSubmit}) => {
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [forcePasswordChange, setForcePasswordChange] = useState(true);
    const [error, setError] = useState("");

    const handleClose = () => {
        setPassword("");
        setConfirmPassword("");
        setForcePasswordChange(true);
        setError("");
        close();
    };

    const handleSubmit = () => {
        if (password !== confirmPassword) {
            setError("הסיסמאות לא תואמות");
            return;
        }
        if (password.length < 4) {
            setError("סיסמה חייבת להכיל לפחות 4 תווים");
            return;
        }
        onSubmit(password, forcePasswordChange);
        handleClose();
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: borders.radius.lg,
                    minWidth: {xs: "90%", sm: "420px"},
                    maxWidth: "480px",
                    overflowY: "auto"
                }
            }}
        >
            <RTL>
                <Box dir="rtl">
                    {/* Header */}
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px 20px",
                        backgroundColor: colors.bg,
                        borderBottom: borders.default
                    }}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <LockIcon sx={{color: colors.textSecondary, fontSize: "20px"}} />
                            <Typography sx={{ fontWeight: 600, fontSize: "16px", color: colors.textPrimary }}>
                                שינוי סיסמה
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleClose}
                            size="small"
                            sx={{ color: colors.textMuted, "&:hover": { backgroundColor: colors.bgHover, color: colors.textPrimary } }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <DialogContent sx={{padding: "24px 20px"}}>
                        <Box sx={{display: "flex", flexDirection: "column", gap: "16px"}}>
                            <Box>
                                <Typography sx={labelSx}>סיסמה חדשה</Typography>
                                <TextField sx={inputSx} fullWidth size="small" type="password"
                                    value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }}
                                    variant="outlined" placeholder="הזן סיסמה חדשה" />
                            </Box>
                            <Box>
                                <Typography sx={labelSx}>אימות סיסמה</Typography>
                                <TextField sx={inputSx} fullWidth size="small" type="password"
                                    value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                                    variant="outlined" placeholder="הזן סיסמה שנית" />
                            </Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={forcePasswordChange}
                                        onChange={(e) => setForcePasswordChange(e.target.checked)}
                                        size="small"
                                        sx={{ color: colors.border, "&.Mui-checked": { color: colors.primary } }}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontSize: "13px", color: colors.textSecondary }}>
                                        חייב את המשתמש להחליף סיסמה בכניסה הבאה
                                    </Typography>
                                }
                                sx={{ margin: 0, alignItems: "center" }}
                            />
                            {error && <Typography sx={errorSx}>{error}</Typography>}
                        </Box>
                    </DialogContent>

                    {/* Footer */}
                    <Box sx={{
                        display: "flex", justifyContent: "flex-start", gap: 1.5,
                        padding: "16px 20px", borderTop: borders.default, backgroundColor: colors.bg
                    }}>
                        <Button
                            onClick={handleSubmit}
                            disabled={!password || !confirmPassword}
                            variant="contained"
                            sx={{
                                backgroundColor: colors.primary, color: colors.white, fontSize: "13px", fontWeight: 600,
                                padding: "8px 24px", borderRadius: borders.radius.md, textTransform: "none",
                                "&:hover": { backgroundColor: colors.primaryDark },
                                "&.Mui-disabled": { backgroundColor: colors.border, color: colors.textMuted }
                            }}
                        >
                            שמור
                        </Button>
                        <Button onClick={handleClose} variant="outlined"
                            sx={{
                                borderColor: colors.border, color: colors.textSecondary, fontSize: "13px", fontWeight: 500,
                                padding: "8px 20px", borderRadius: borders.radius.md, textTransform: "none",
                                "&:hover": { borderColor: colors.textSecondary, backgroundColor: colors.bgHover }
                            }}>
                            ביטול
                        </Button>
                    </Box>
                </Box>
            </RTL>
        </Dialog>
    );
};

export default ChangePasswordDialog;
